import React, { useState, useEffect } from 'react';
import CustomTable from '../../components/Tables/CustomTable';
import TableActionButtons from '../../components/Buttons/TableActionButtons';
import CustomModal from '../../components/Modals/CustomModal';
import ConfirmModal from '../../components/Modals/ConfirmModal';
import ToastManager from '../../components/Toast/ToastManager';
import CreateUserButton from '../../components/Buttons/CreateUserButton';
import api from '../../services/api';
import { useNavigate } from 'react-router-dom';
import SearchBar from '../../components/SearchBar/SearchBar';
import { useToast } from '../../hooks/useToast';

const UserIndexPage = () => {
  const [data, setData] = useState([]);
  const [viewItem, setViewItem] = useState(null);
  const [isViewModalOpen, setViewModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const { toasts, removeToast, showSuccess, showError } = useToast();
  const [isConfirmModalOpen, setConfirmModalOpen] = useState(false);
  const [userForModalConfirmation, setUserForModalConfirmation] = useState(null);
  const [actionType, setActionType] = useState('');
  // Função para buscar os dados
  const fetchData = async (page = 1, size = 10, searchTerm = "") => {
    setLoading(true);
    setError(null);

    try {
      // Realiza a requisição para o backend com o token no cabeçalho
      const response = await api.get('/users/list', {
        params: { page, size, search: searchTerm.trim() }
      });

      setCurrentPage(page);
      setHasNextPage(response.data.length === size);

      // Verifica se a resposta contém dados
      if (response.data) {
        setData(response.data);
      } else {
        setError('Nenhum usuário encontrado');
      }
    } catch (err) {
      if (err.response && err.response.status === 404) {
        setError('Nenhum usuário encontrado');
      } else if (err.response && err.response.status === 403) {
        setError('Usuário não autorizado. Por favor, faça login.');
        setTimeout(() => {
          navigate('/');
        }, 3000);
      } else if (err.response && err.response.status === 401) {
        setError('Usuário não autorizado. Por favor, faça login.');
        setTimeout(() => {
          navigate('/');
        }, 3000);
      } else {
        setError('Erro ao carregar os dados');
      }
    } finally {
      setLoading(false);
    }
  };

  // Buscando os dados para montar o componente
  useEffect(() => {
    fetchData();
  }, []);

  const handlePageChange = (page) => {
    fetchData(page);
  };

  const handleView = (item) => {
    setViewItem(item);
    setViewModalOpen(true);
  };

  const handleDelete = async (user) => {
    try {
      await api.patch(`/users/${user.cpf}/desabilitar`);
      showSuccess("Usuário desabilitado com sucesso!");
      fetchData(currentPage);
    } catch (error) {
      console.error("Erro ao desabilitar usuário", error);
      showError("Erro ao desabilitar usuário");
    }
  };

  const handleUndelete = async (user) => {
    try {
      await api.patch(`/users/${user.cpf}/habilitar`);
      showSuccess("Usuário habilitado com sucesso!");
      fetchData(currentPage);
    } catch (error) {
      console.error("Erro ao habilitar usuário", error);
      showError("Erro ao habilitar usuário");
    }
  };



  const handleResetPassword = async (user) => {
    try {
      await api.put(`/users/${user.cpf}/password-reset`);
      showSuccess("Senha resetada com sucesso! Um e-mail foi enviado ao usuário.");
    } catch (error) {
      console.error("Erro ao resetar senha:", error);
      showError("Erro ao resetar senha.");
    }
  };

  const handleOpenConfirmModal = (user, action) => {
    setUserForModalConfirmation(user);
    setActionType(action);
    setConfirmModalOpen(true);
  };

  const handleConfirmAction = () => {
    if (userForModalConfirmation && actionType) {
      switch (actionType) {
        case 'disable':
          handleDelete(userForModalConfirmation);
          break;
        case 'enable':
          handleUndelete(userForModalConfirmation);
          break;
        case 'resetPassword':
          handleResetPassword(userForModalConfirmation);
          break;
      }
      setUserForModalConfirmation(null);
      setActionType('');
    }
  };

  return (
    <div>
      <CreateUserButton onClick={() => (window.location.href = '/user/create')} />
      {error && <p style={{ color: '#1a1a1a' }}>{error}</p>}
      {loading ? (
        <p>Carregando...</p>
      ) : (
        <>
          <SearchBar
            placeholder={'Buscar por CPF ou nome...'}
            onSearch={(term) => fetchData(1, 10, term)}
          />
          <CustomTable
            columns={[
              { field: 'nome', headerName: 'Nome do Usuário' },
              { field: 'cpf_formatado', headerName: 'CPF do Usuário' },
              { field: 'email', headerName: 'E-mail do Usuário' },
              { field: 'perfil', headerName: 'Perfil do Usuário' },
              { field: 'municipio', headerName: 'Município do Usuário' },
              { field: 'regional', headerName: 'Regional do Usuário' },
              { field: 'status_texto', headerName: 'Status do Usuário' }

            ]}
            data={data.map(user => ({
              ...user,
              perfil: Array.isArray(user.perfil) && user.perfil.length > 0
                ? user.perfil.map(p => p.nome).join(", ")
                : "Sem perfil",
              municipio: user.municipio ? user.municipio.nome : "N/A",
              regional: user.regional ? user.regional.nome : "N/A",
              status_texto: user.status_habilitado ? "Ativo" : "Inativo",
              cpf_formatado: user.cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4'),

            }))}
            renderActions={(row) => (
              <TableActionButtons
                showDelete={row.status_habilitado}
                showUndelete={!row.status_habilitado}
                onView={() => handleView(row)}
                onEdit={() => navigate(`/user/edit/${row.cpf}`)}
                onDelete={() => handleOpenConfirmModal(row, 'disable')}
                onUndelete={() => handleOpenConfirmModal(row, 'enable')}
                onResetPassword={() => handleOpenConfirmModal(row, 'resetPassword')}
              />
            )}
            currentPage={currentPage}
            hasNextPage={hasNextPage}
            onPageChange={handlePageChange}
          />
        </>
      )}

      <CustomModal
        isOpen={isViewModalOpen}
        onClose={() => setViewModalOpen(false)}
        title="Detalhes do Usuário"
      >
        {viewItem && (
          <div>
            <p><strong>Nome:</strong> {viewItem.nome}</p>
            <p><strong>CPF:</strong> {viewItem.cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')}</p>
            <p><strong>Email:</strong> {viewItem.email}</p>
            <p><strong>Perfil:</strong> {viewItem.perfil}</p>
            <p><strong>Município:</strong> {viewItem.municipio}</p>
            <p><strong>Regional:</strong> {viewItem.regional}</p>
            <p><strong>Status:</strong> {viewItem.status_habilitado ? "Ativo" : "Inativo"}</p>
          </div>
        )}
      </CustomModal>

      <ConfirmModal
        isOpen={isConfirmModalOpen}
        onClose={() => {
          setConfirmModalOpen(false);
          setUserForModalConfirmation(null);
          setActionType('');
        }}
        onConfirm={handleConfirmAction}
        title="Confirmação"
        message={
          userForModalConfirmation && actionType
            ? {
                disable: `Deseja desabilitar o usuário "${userForModalConfirmation.nome}"?`,
                enable: `Deseja habilitar o usuário "${userForModalConfirmation.nome}"?`,
                resetPassword: `Deseja realmente resetar a senha de ${userForModalConfirmation.nome}?`
              }[actionType]
            : ''
        }
        confirmText={
          actionType === 'disable' ? 'Desabilitar' :
          actionType === 'enable' ? 'Habilitar' :
          actionType === 'resetPassword' ? 'Resetar Senha' : 'Confirmar'
        }
        confirmButtonColor={
          actionType === 'enable' ? '#595959' : '#1a1a1a'
        }
      />
        {/* Toast Manager para notificações */}
      <ToastManager toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default UserIndexPage;
